import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Target, Users, TrendingUp, Heart, ArrowRight } from 'lucide-react';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { TeamSection } from '../components/static/TeamSection';
import { StatsGrid } from '../components/static/StatsGrid';
import { Button } from '../components/shared/Button';
import { SectionHeading } from '../components/shared/SectionHeading';

export const AboutPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const values = [
    {
      icon: Target,
      titleKey: 'about.values.goalOriented.title',
      descriptionKey: 'about.values.goalOriented.description',
      color: 'text-primary'
    },
    {
      icon: Users,
      titleKey: 'about.values.accessible.title',
      descriptionKey: 'about.values.accessible.description',
      color: 'text-secondary'
    },
    {
      icon: TrendingUp,
      titleKey: 'about.values.resultOriented.title',
      descriptionKey: 'about.values.resultOriented.description',
      color: 'text-green-500'
    },
    {
      icon: Heart,
      titleKey: 'about.values.involved.title',
      descriptionKey: 'about.values.involved.description',
      color: 'text-red-500'
    }
  ];

  return (
    <div className="page-shell">
      <Header />

      <section className="section-shell">
        <div className="section-container">
          <div className="surface-card-soft p-8 md:p-12">
            <SectionHeading
              align="center"
              eyebrow={t('about.eyebrow')}
              title={t('about.heroTitle')}
              subtitle={t('about.heroSubtitle')}
            />
            <div className="mt-8 flex justify-center">
              <Button
                variant="primary"
                size="large"
                icon={<ArrowRight className="h-5 w-5" />}
                onClick={() => navigate('/register')}
              >
                {t('about.missionCta')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell">
        <div className="section-container">
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-14">
            <div>
              <span className="section-eyebrow">{t('about.mission')}</span>
              <h2 className="section-title">{t('about.missionTitle')}</h2>
              <div className="mt-5 space-y-4 text-base leading-relaxed text-slate-700 md:text-lg">
                <p>
                  {t('about.missionText1')}
                </p>
                {/* XSS: i18n string only. Do not pass user or external content without sanitization. */}
                <p dangerouslySetInnerHTML={{ __html: t('about.missionText2') }} />
                <p>
                  {t('about.missionText3')}
                </p>
              </div>
              <Button 
                variant="primary" 
                size="large" 
                className="mt-8"
                icon={<ArrowRight className="h-5 w-5" />}
                onClick={() => navigate('/register')}
              >
                {t('about.missionCta')}
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div 
                    key={index}
                    className="surface-card p-6"
                  >
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10">
                      <Icon className={`h-5 w-5 ${value.color}`} />
                    </div>
                    <h3 className="mb-2 text-lg font-bold text-ink">{t(value.titleKey)}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">
                      {t(value.descriptionKey)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <TeamSection />

      <section className="section-shell">
        <div className="section-container">
          <SectionHeading
            align="center"
            eyebrow={t('about.impactEyebrow')}
            title={t('about.impactTitle')}
          />
          <div className="mt-10">
          <StatsGrid />
          </div>
          <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-slate-600">
            {t('about.impactNote')}
          </p>
        </div>
      </section>

      <section className="section-shell">
        <div className="section-container">
          <div className="surface-card-soft px-6 py-12 text-center md:px-12">
          <h2 className="text-4xl font-bold text-ink md:text-5xl">
            {t('about.ctaTitle')}
          </h2>
          <p className="mx-auto mb-8 mt-4 max-w-2xl text-lg text-slate-600">
            {t('about.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="primary" 
              size="large"
              onClick={() => navigate('/try-free')}
            >
              {t('about.ctaPrimary')}
            </Button>
            <Button 
              variant="secondary" 
              size="large"
              onClick={() => navigate('/contact')}
            >
              {t('about.ctaSecondary')}
            </Button>
          </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
